import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { PausaComMotivo } from './PausaComMotivo';
import {
  NAV_AREAS,
  NAV_RODAPE,
  ENVIRONMENT_LABEL,
  OPERATIONAL_STATE_LABEL,
  OPERATIONAL_STATE_EXPLICACAO,
  OPERATIONAL_STATE_DOT,
  type NavArea,
  type OperationalState,
  type SystemState,
} from '../lib/navigation';

/**
 * Casca do Console.
 *
 * O desenho e o do layout entregue em 17/09/2026: barra lateral escura de
 * 250 px com a marca no topo e a ajuda ancorada no rodape, cabecalho de 76 px
 * com titulo, pilula de estado e o bloco de quem esta logado.
 *
 * O que o desenho NAO podia custar: Back Office SPEC §6 exige, em TODA tela,
 * ambiente, estado do sistema, frescor do dado, ator e papel, acesso
 * persistente a caixa de atencao e o botao de pausa para quem tem permissao.
 * Cada um desses tem lugar no layout novo, e nenhum foi perdido na troca:
 *
 * | §6 exige           | onde ficou no layout entregue          |
 * | ------------------ | -------------------------------------- |
 * | ambiente           | selo ao lado do titulo                  |
 * | estado do sistema  | a pilula "System Online" do desenho     |
 * | frescor do dado    | segunda linha da pilula                 |
 * | ator e papel       | o bloco de avatar a direita             |
 * | caixa de atencao   | o sino com contagem                     |
 * | pausa de emergencia| o botao vermelho ao lado do avatar      |
 *
 * "Producao e nao-producao devem ser inconfundiveis" continua valendo: o
 * ambiente aparece com TEXTO, nao so com cor.
 */

export type ShellContext = {
  readonly environment: string;
  /** Eixo de PAUSA, usado so pelo botao de parar e retomar. */
  readonly systemState: SystemState;
  /** O que a pessoa LE: pausa mais efeito externo, derivado no servidor. */
  readonly operationalState: OperationalState;
  /** Alguma coisa dirige a cadeia? Desligado, nenhum pipeline anda. */
  readonly motorEnabled: boolean;
  readonly dataFreshness: string | null;
  readonly actorName: string;
  readonly actorRole: string;
  readonly scopes: readonly string[];
  readonly appVersion: string;
};

/** Iniciais para o avatar, sem foto: o layout mostra um circulo com a pessoa. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

function ItemDeNavegacao({
  area,
  permitido,
  aoNavegar,
}: {
  readonly area: NavArea;
  readonly permitido: boolean;
  readonly aoNavegar: () => void;
}) {
  return (
    <li>
      <NavLink
        to={area.path}
        /*
          `end` marca ativa SO a rota exata, e nao os descendentes.

          Sem ele, duas areas aparecem marcadas ao mesmo tempo assim que uma
          delas e prefixo da outra. Nao e cosmetica: o destaque e como a pessoa
          confere que clicou onde queria.
        */
        end
        onClick={aoNavegar}
        className={({ isActive }) =>
          [
            'flex items-center justify-between gap-2 rounded-[10px] px-3 py-2.5 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-accent',
            isActive
              ? 'bg-nav-active font-semibold text-accent-ink'
              : 'text-ink-muted hover:bg-surface-raised hover:text-ink',
            permitido ? '' : 'opacity-60',
          ].join(' ')
        }
      >
        <span>{area.label}</span>
        {!area.implemented && (
          /*
            Area sem dado e dita na propria navegacao, e nao descoberta depois
            do clique. Quando a fase foi decidida, o selo diz o numero; quando
            nao foi, ele diz que a tela ainda nao tem fonte, que e a verdade.
            Inventar um numero de fase para preencher o selo seria pior.
          */
          <span className="shrink-0 rounded-full border border-line px-1.5 py-0.5 text-[10px] text-ink-faint">
            {area.availableFromPhase === undefined ? 'sem dado' : `fase ${area.availableFromPhase}`}
          </span>
        )}
        {!permitido && <span className="sr-only"> (sem acesso)</span>}
      </NavLink>

      {/*
        Sub-areas PRECISAM aparecer aqui. Rota entregue sem nada na tela que a
        chame nao esta entregue, e depois do layout de 17/09 varias telas que
        eram area de primeiro nivel viraram filhas: se elas nao aparecessem,
        a entrega teria ESCONDIDO tela que funcionava. Um teste varre os filhos
        exigindo link alcancavel para cada um.
      */}
      {area.children && area.children.length > 0 ? (
        <ul className="ml-3 mt-0.5 border-l border-line pl-2">
          {area.children.map((filho) => (
            <li key={filho.id}>
              <NavLink
                to={filho.path}
                /*
                  `end` tambem nas FILHAS, e nao so nas areas.

                  Sem ele, "Audiencia e crescimento" (`/audiencia`) aparece
                  marcada junto com "Audience & Lists" (`/audiencia/listas`),
                  porque o caminho de uma e prefixo do da outra. Enquanto
                  `/audiencia` era area, o `end` dela ja cobria isso; ao virar
                  filha em 17/09 o caso voltou pelo outro lado.
                */
                end
                onClick={aoNavegar}
                className={({ isActive }) =>
                  [
                    'flex items-center justify-between gap-2 rounded-[8px] px-2 py-1.5 text-xs',
                    'focus:outline-none focus:ring-2 focus:ring-accent',
                    isActive
                      ? 'bg-surface-raised font-semibold text-accent-ink'
                      : 'text-ink-faint hover:bg-surface-raised hover:text-ink-muted',
                  ].join(' ')
                }
              >
                <span>{filho.label}</span>
                {filho.implemented === false && (
                  <span className="shrink-0 text-[10px] text-ink-faint">
                    {filho.availableFromPhase === undefined
                      ? 'sem dado'
                      : `fase ${filho.availableFromPhase}`}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function Shell({ context }: { context: ShellContext }) {
  // Abaixo de 1024 px a navegacao vira menu compacto (Wireframes §14).
  const [menuOpen, setMenuOpen] = useState(false);
  const canPause = context.scopes.includes('system:pause');
  /*
    `paused` e o sistema INTEIRO parado; `partially_paused` e alguma categoria
    parada com o sistema de pe.

    So o primeiro troca o botao para "Retomar sistema". Tratar o parcial como
    pausado esconderia o botao de PARAR justamente quando uma categoria ja
    parou e o resto continua gastando, que e a hora em que ele mais serve.
  */
  const systemPaused = context.systemState === 'paused';
  const isProduction = context.environment === 'production';
  const fechaMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[250px_1fr]">
      {/* Pular para o conteudo: primeiro item tabulavel (WCAG 2.4.1). */}
      <a
        href="#conteudo-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2 focus:ring-2 focus:ring-accent"
      >
        Pular para o conteúdo
      </a>

      <nav
        id="navegacao-principal"
        aria-label="Navegação principal"
        className={[
          menuOpen ? 'block' : 'hidden',
          'border-line bg-surface-muted/95 px-3.5 py-4',
          'lg:sticky lg:top-0 lg:block lg:h-screen lg:overflow-y-auto lg:border-r',
          'border-b lg:border-b-0',
        ].join(' ')}
      >
        {/* Marca, como o layout desenha: bloco com a sigla e o nome em duas linhas. */}
        <div className="flex items-center gap-3 px-2 pb-5 pt-1">
          <span
            aria-hidden="true"
            className="grid h-9 w-9 place-items-center rounded-[11px] border border-line-strong bg-surface-raised text-sm font-extrabold"
          >
            FI
          </span>
          <span className="leading-tight">
            <b className="block text-lg tracking-[0.06em]">FERRARI</b>
            <span className="block text-[10px] tracking-[0.28em] text-ink-muted">INSTAGRAM AI</span>
          </span>
        </div>

        <ul className="flex flex-col gap-1">
          {NAV_AREAS.map((area) => (
            <ItemDeNavegacao
              key={area.id}
              area={area}
              permitido={context.scopes.includes(area.requiredScope)}
              aoNavegar={fechaMenu}
            />
          ))}
        </ul>

        {/*
          Ajuda fica ancorada embaixo, separada por um vao, como no desenho.
          `mt-auto` nao serve aqui porque a barra rola; o vao explicito e o que
          reproduz o destaque sem depender da altura da janela.
        */}
        <ul className="mt-8 border-t border-line pt-3">
          {NAV_RODAPE.map((area) => (
            <ItemDeNavegacao
              key={area.id}
              area={area}
              permitido={context.scopes.includes(area.requiredScope)}
              aoNavegar={fechaMenu}
            />
          ))}
        </ul>
      </nav>

      <div className="min-w-0">
        <header className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-line px-4 py-3 lg:min-h-[76px] lg:px-6 lg:py-0">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="navegacao-principal"
            className="rounded-[10px] border border-line-strong px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent lg:hidden"
          >
            {menuOpen ? 'Fechar menu' : 'Menu'}
          </button>

          <div className="min-w-0">
            {/*
              O nome do produto no cabecalho NAO e `h1`.

              O `h1` de um documento e o titulo da TELA, e cada pagina ja tem o
              seu. Com o cabecalho tambem em `h1`, toda tela passava a ter dois,
              e um leitor de tela anuncia dois titulos de documento sem dizer
              qual e o da pagina. Aqui e uma marca de banner: estilo de titulo,
              semantica de paragrafo.
            */}
            <p className="flex flex-wrap items-center gap-2 text-base font-semibold">
              Configuration &amp; Governance Console
              {/*
                A versao e a do binario que esta servindo, e nao um rotulo
                escrito a mao. O PNG de referencia traz "v1.4" desenhado; copiar
                aquele texto poria um numero fixo numa tela que existe
                justamente para dizer o que esta no ar.
              */}
              <span className="rounded-full border border-line bg-surface-raised px-2 py-0.5 text-[11px] font-normal text-ink-muted">
                {context.appVersion}
              </span>
              {/*
                Ambiente com TEXTO, nao apenas cor. Producao ganha o tratamento
                invertido para ser inconfundivel mesmo em monocromatico.
              */}
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide ${
                  isProduction
                    ? 'border-2 border-ink bg-ink text-surface'
                    : 'border border-line-strong bg-surface-sunken text-ink-muted'
                }`}
                data-testid="ambiente"
              >
                {ENVIRONMENT_LABEL[context.environment] ?? context.environment.toUpperCase()}
              </span>
            </p>
            <p className="text-xs text-ink-muted">
              Configure. Controle. Pause. Retome com segurança.
            </p>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-3">
            {/* A pilula de estado do desenho: ponto colorido mais o estado escrito. */}
            <span
              className="rounded-[10px] border border-line bg-surface px-3 py-1.5 leading-tight"
              data-testid="estado-sistema"
              title={OPERATIONAL_STATE_EXPLICACAO[context.operationalState]}
            >
              <span className="flex items-center gap-2 text-xs font-semibold">
                {/*
                  O ponto acompanha o TEXTO, e verde e reservado para operar de
                  verdade: verde com o sistema em teste foi o que fez a tela
                  parecer no ar.
                */}
                <span
                  aria-hidden="true"
                  className={`inline-block h-2 w-2 shrink-0 rounded-full ${OPERATIONAL_STATE_DOT[context.operationalState]}`}
                />
                Sistema: {OPERATIONAL_STATE_LABEL[context.operationalState]}
              </span>
              {/* Frescor: ausente aparece como texto explicito, nunca em branco. */}
              <span className="block text-[11px] text-ink-muted" data-testid="frescor">
                {context.dataFreshness
                  ? `Dado até ${context.dataFreshness}`
                  : 'Frescor: não configurado'}
              </span>
            </span>

            <NavLink
              to="/atencao"
              className="rounded-[10px] border border-line px-2.5 py-1.5 text-xs text-ink-muted hover:bg-surface-raised hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            >
              Caixa de atenção
            </NavLink>

            <span className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="grid h-8 w-8 place-items-center rounded-full border border-line-strong bg-surface-raised text-[11px] font-bold"
              >
                {iniciais(context.actorName)}
              </span>
              <span className="text-xs leading-tight">
                <b className="block">{context.actorName}</b>
                <span className="block text-ink-muted">{context.actorRole}</span>
              </span>
            </span>

            {/*
              Pausa de emergencia SO para quem tem o escopo. Esconder nao e
              autorizacao (o servidor decide de novo), mas mostrar um botao que
              sempre nega e ruido operacional na hora do incidente.

              Ela passa pela fronteira de comando que ja existe (`PauseSystem`),
              e nao por uma rota nova: e la que moram o ator, o motivo, a
              idempotencia e a auditoria.
            */}
            {canPause && (
              <PausaComMotivo
                pausado={systemPaused}
                commandType={{ parar: 'PauseSystem', retomar: 'ResumeSystem' }}
                targetRef={{ object_type: 'System', object_id: 'system' }}
                payload={{ scope: 'system', scope_id: null }}
                rotuloParar="Pausar sistema"
                rotuloRetomar="Retomar sistema"
                explicacaoAoParar="Nenhum efeito externo novo acontece enquanto o sistema estiver pausado: nada publica, nada envia, nada gasta. O que ja esta no ar continua no ar."
                explicacaoAoRetomar="Retomar NAO religa nada sozinho: cada coisa parada volta por decisao de alguem, e a retomada passa pela verificacao de prontidao."
                idDoCampo="sistema"
                idDoBotao="botao-pausa"
                destaque
              />
            )}
          </div>
        </header>

        <main id="conteudo-principal" className="min-w-0 p-4 lg:p-6">
          <Outlet />
        </main>

        <footer className="border-t border-line px-4 py-3 text-xs text-ink-faint lg:px-6">
          Versão {context.appVersion} · Console SPEC v1.5 · identificadores de código disponíveis no
          detalhe de cada item
        </footer>
      </div>
    </div>
  );
}
